import { Component, inject, OnInit, signal } from '@angular/core';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { map, Observable, switchMap } from 'rxjs';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  TextFieldWithErrorComponent,
  FormSectionComponent,
  ButtonComponent,
  DropdownFieldComponent,
  DropdownFieldWithErrorComponent,
  DropdownOption,
  CheckboxFieldComponent,
  AlertComponent,
  FileUploadFieldComponent,
  TextareaFieldComponent,
} from 'htx-ui-library';

import { OrganizationMemberDetails, ActivityState } from '../../../../../services/organizations/organization.model';
import { OrganizationService } from '../../../../../services/organizations/organization.service';
import { RepresentativeChangeService } from '../../../../../services/representative-change/representative-change.service';
import { NotificationService } from '../../../../../services/form/notification.service';
import { FormErrorMessagesService } from '../../../../../services/form/form-error-messages.service';
import { buildSalutationOptions } from '../../../../../services/form/salutation-options';
import { NavigationService } from '../../../../../services/navigation.service';
import { ProtectedContentComponent } from '../../../../organisms/protected-content/protected-content.component';
import { RoleService } from '../../../../../services/role/role.service';
import { maxRoleLevel, ROLE_LEVELS } from '../../../../../services/auth/auth.role';

/** All assignable roles (excluding HTX_PORTAL_BACKEND which is a system role), ordered by level ascending.
 *  ORGANIZATION_REPRESENTATIVE is excluded because representative changes go through a dedicated approval flow. */
const ALL_ASSIGNABLE_ROLES = [
  'ORGANIZATION_USER',
  'ORGANIZATION_ADMIN',
  'DATASPACE_AUTHORITY',
] as const;

type AssignableRole = (typeof ALL_ASSIGNABLE_ROLES)[number];

@Component({
  selector: 'app-organization-member-edit-page',
  imports: [
    TranslocoDirective,
    TranslocoPipe,
    ReactiveFormsModule,
    TextFieldWithErrorComponent,
    DropdownFieldComponent,
    DropdownFieldWithErrorComponent,
    CheckboxFieldComponent,
    AlertComponent,
    FormSectionComponent,
    ButtonComponent,
    ProtectedContentComponent,
    FileUploadFieldComponent,
    TextareaFieldComponent,
  ],
  templateUrl: './organization-member-edit-page.component.html',
  styleUrl: './organization-member-edit-page.component.scss'
})
export class OrganizationMemberEditPageComponent implements OnInit {
  private fb: FormBuilder = inject(FormBuilder);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private organizationService = inject(OrganizationService);
  private representativeChangeService = inject(RepresentativeChangeService);
  private notificationService = inject(NotificationService);
  private translocoService = inject(TranslocoService);
  private navigationService = inject(NavigationService);
  private roleService = inject(RoleService);
  readonly errorMessagesService = inject(FormErrorMessagesService);

  requiredRoles = this.navigationService.getAccessRoles('admin/organizations/members/edit');
  fieldLabels: { [key: string]: string } = {};

  /** All roles shown in the UI as checkboxes */
  assignableRoles = ALL_ASSIGNABLE_ROLES;


  readonly salutationOptions = signal<DropdownOption[]>(buildSalutationOptions(this.translocoService));

  statusOptions = signal<DropdownOption[]>([]);

  /** Numeric level of the currently logged-in user's strongest role */
  callerLevel = 0;

  /** True when the logged-in user is editing their own member record */
  readonly isSelfEdit = signal(false);

  /** True when the member being edited is the current organisation representative */
  readonly isCurrentRepresentative = signal(false);

  /** True when the caller is the current representative (can initiate change) */
  readonly isCallerRepresentative = signal(false);

  /** True when viewing another member and caller is representative (shows rep change section) */
  readonly showRepChangeSection = signal(false);

  /** Signal for representative change request submission state */
  readonly repChangeSubmitting = signal(false);

  /** Form controls for representative change request */
  repChangeReasoningCtrl = new FormControl('');
  repChangeDocumentCtrl = new FormControl<File | null>(null, Validators.required);

  private callerUserId = '';

  organizationId$ = this.activatedRoute.paramMap.pipe(map((data) => data.get('organizationId')!));
  memberId$ = this.activatedRoute.paramMap.pipe(map((data) => data.get('memberId')!));

  data$: Observable<OrganizationMemberDetails> = this.activatedRoute.paramMap.pipe(
    switchMap((params) => {
      const memberId = params.get('memberId')!;
      return this.organizationService.findMemberDetails(memberId);
    })
  );

  form = this.fb.group({
    title: ['', [Validators.maxLength(50)]],
    firstName: ['', [Validators.required, Validators.maxLength(50)]],
    lastName: ['', [Validators.required, Validators.maxLength(50)]],
    email: ['', [Validators.required, Validators.email, Validators.maxLength(100)]],
    position: ['', [Validators.maxLength(100)]],
    phoneNumber: ['', [Validators.maxLength(20)]],
    roles: this.fb.group({
      ORGANIZATION_USER: [false],
      ORGANIZATION_ADMIN: [false],
      DATASPACE_AUTHORITY: [false],
    }),
    activityState: ['ACTIVE', [Validators.required]],
  });

  ngOnInit(): void {
    // Determine caller level and identity to decide which role checkboxes are editable
    this.callerLevel = maxRoleLevel(this.roleService.getUserRoles$().value);
    this.callerUserId = this.roleService.getCallerUserId();
    console.log('User roles:', this.roleService.getUserRoles$().value, 'Caller level:', this.callerLevel, 'Caller ID:', this.callerUserId);

    // Disable role checkboxes for roles the caller cannot assign (level-based)
    const rolesGroup = this.form.get('roles')!;
    ALL_ASSIGNABLE_ROLES.forEach((role) => {
      const ctrl = rolesGroup.get(role);
      if (ctrl) {
        if (this.isRoleDisabled(role)) {
          ctrl.disable();
        } else {
          ctrl.enable();
        }
      }
    });

    // Initialize status options with translations
    this.statusOptions.set([
      { value: 'ACTIVE', label: this.translocoService.translate('organization.member.status.active') },
      { value: 'INACTIVE', label: this.translocoService.translate('organization.member.status.inactive') },
    ]);

    // Load field labels from translations
    const controlNames = ['title', 'firstName', 'lastName', 'email', 'position', 'phoneNumber'];
    controlNames.forEach(controlName => {
      this.fieldLabels[controlName] = this.translocoService.translate(`organization.member.edit.controls.${controlName}.label`);
    });

    // Populate form with data
    this.data$.subscribe((member) => {
      this.form.patchValue({
        title: member.title || '',
        firstName: member.firstName,
        lastName: member.lastName,
        email: member.email,
        position: member.position,
        phoneNumber: member.phoneNumber || '',
        activityState: member.activityState || 'ACTIVE',
      });

      // Determine if the caller is editing their own record
      const selfEdit = !!(member.iamId && member.iamId === this.callerUserId);
      this.isSelfEdit.set(selfEdit);

      // Determine if this member is currently the representative
      const memberRolesUpper = member.roles.map(r => r.toUpperCase());
      const isRep = memberRolesUpper.includes('ORGANIZATION_REPRESENTATIVE');
      this.isCurrentRepresentative.set(isRep);

      // Determine if the caller is a representative (can initiate rep change)
      const callerRoles = this.roleService.getUserRoles$().value.map((r: string) => r.toUpperCase());
      const callerIsRep = callerRoles.includes('ORGANIZATION_REPRESENTATIVE');
      this.isCallerRepresentative.set(callerIsRep);

      // Show rep change section when: caller is representative, not self-edit, and target is not current rep
      this.showRepChangeSection.set(callerIsRep && !selfEdit && !isRep);

      // Set role checkboxes based on member roles
      ALL_ASSIGNABLE_ROLES.forEach(role => {
        rolesGroup.get(role)?.setValue(memberRolesUpper.includes(role));
      });

      // Disable checkboxes: self-edit locks all; representative role locked if currently held
      ALL_ASSIGNABLE_ROLES.forEach(role => {
        const ctrl = rolesGroup.get(role);
        if (!ctrl) return;
        if (selfEdit || this.isRoleDisabled(role as AssignableRole)) {
          ctrl.disable();
        } else {
          ctrl.enable();
        }
      });
    });
  }

  /**
   * Returns true if the checkbox for the given role should be shown as disabled.
   * - ORGANIZATION_USER is always required and can never be removed
   * - Self-edit: all role checkboxes locked
   * - Current representative: REPRESENTATIVE checkbox locked (must be transferred, not removed)
   * - Caller level too low for the target role
   */
  isRoleDisabled(role: AssignableRole): boolean {
    if (role === 'ORGANIZATION_USER') return true;
    if (this.isSelfEdit()) return true;
    const roleLevel = ROLE_LEVELS[role.toLowerCase()] ?? -1;
    return this.callerLevel < roleLevel;
  }

  save(): void {
    if (this.form.invalid) {
      return;
    }

    this.activatedRoute.paramMap.subscribe((params) => {
      const memberId = params.get('memberId')!;

      const formValue = this.form.getRawValue();

      // Map role checkboxes back to role string array.
      // ORGANIZATION_USER is always included – it's a required base role.
      // ORGANIZATION_REPRESENTATIVE is preserved from the original member data since
      // it is no longer editable via checkboxes (managed via dedicated change request flow).
      // getRawValue() returns values of disabled controls too, so locked roles
      // during self-edit are correctly preserved and always sent to the backend.
      const selectedRoles: string[] = [
        'ORGANIZATION_USER',
        ...ALL_ASSIGNABLE_ROLES.filter(role => role !== 'ORGANIZATION_USER' && formValue.roles[role]),
        ...(this.isCurrentRepresentative() ? ['ORGANIZATION_REPRESENTATIVE'] : []),
      ];

      const memberData: Partial<OrganizationMemberDetails> = {
        title: formValue.title || undefined,
        firstName: formValue.firstName ?? '',
        lastName: formValue.lastName ?? '',
        email: formValue.email ?? '',
        position: formValue.position ?? '',
        phoneNumber: formValue.phoneNumber || undefined,
        roles: selectedRoles,
        activityState: formValue.activityState as ActivityState,
      };

      this.organizationService.updateMember(memberId, memberData).subscribe({
        next: () => {
          this.notificationService.showSuccess('organization.member.edit.saveSuccess', 5000);
          this.backToDetail();
        },
        error: () => {
          this.notificationService.showError('organization.member.edit.saveError', 5000);
        }
      });
    });
  }

  backToDetail(): void {
    this.activatedRoute.paramMap.subscribe((params) => {
      const organizationId = params.get('organizationId')!;
      const memberId = params.get('memberId')!;
      void this.router.navigate(['admin', 'organizations', 'details', organizationId, 'members', memberId]);
    });
  }

  submitRepresentativeChange(): void {
    const document = this.repChangeDocumentCtrl.value;
    if (!document) {
      this.notificationService.showError('representativeChange.create.documentRequired', 5000);
      return;
    }

    this.repChangeSubmitting.set(true);
    const reasoning = this.repChangeReasoningCtrl.value || '';

    this.activatedRoute.paramMap.subscribe((params) => {
      const memberId = params.get('memberId')!;
      this.representativeChangeService.createRequest(memberId, reasoning, document).subscribe({
        next: () => {
          this.notificationService.showSuccess('representativeChange.create.success', 5000);
          this.repChangeSubmitting.set(false);
          this.backToDetail();
        },
        error: () => {
          this.notificationService.showError('representativeChange.create.error', 5000);
          this.repChangeSubmitting.set(false);
        }
      });
    });
  }
}
